import React, { useState } from 'react';
import { keyframes } from '@emotion/react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Divider,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  NotificationsOutlined as NotificationIcon,
  Error as CriticalIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  NotificationsActive as AlertIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logoutUser } from '../store/slices/authSlice';
import Navigation from './Navigation';
import { useAlerts } from '../modules/alert/hooks/useAlerts';
import { Alert } from '../types/alert';
import { handleAlertClick } from '../modules/alert/utils/alertProcessors';

const shakeAnimation = keyframes`
  0%, 100% { transform: rotate(0deg); }
  15%, 45%, 75% { transform: rotate(-8deg); }
  30%, 60%, 90% { transform: rotate(8deg); }
`;

const drawerWidth = 240;
const collapsedDrawerWidth = 72;

interface MainLayoutProps {
  children: React.ReactNode;
}

type AlertLike = Alert & {
  id?: number | string;
  alert_id?: number | string;
  log?: { level?: string };
  severity?: string;
  message?: string;
  robot?: string;
  robot_id?: string;
  museum?: string;
  museum_code?: string;
  timestamp?: string;
  ts?: string;
  notificationText?: string;
};

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [alertMenuAnchor, setAlertMenuAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);

  // Alert monitoring for header badge
  const { alerts, getAlertStats, markAlertsViewed } = useAlerts();
  const [stats, setStats] = useState({
    total: 0,
    critical: 0,
    warning: 0,
    info: 0,
    low: 0,
  });

  React.useEffect(() => {
    getAlertStats().then(setStats);
  }, [alerts, getAlertStats]);

  const hasActiveVideoCall = React.useMemo(() => {
    const videoStartAlerts = alerts.filter(a => a.task === 'alert_robot_video_start');
    const videoEndAlerts = alerts.filter(a => a.task === 'alert_robot_video_end');
    return videoStartAlerts.some(startAlert => {
      const startTime = new Date(startAlert.ts).getTime();
      return !videoEndAlerts.some(
        endAlert => endAlert.robot === startAlert.robot && new Date(endAlert.ts).getTime() > startTime,
      );
    });
  }, [alerts]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/login');
  };

  const handleAlertMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAlertMenuAnchor(event.currentTarget);
  };

  const handleAlertMenuClose = () => {
    setAlertMenuAnchor(null);
  };

  const formatAlertTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      return `${diffHours}h ago`;
    } catch (error) {
      return 'Unknown';
    }
  };

  const getViewedAlerts = () => {
    try {
      return new Set(JSON.parse(localStorage.getItem('viewedAlerts') || '[]'));
    } catch (error) {
      return new Set();
    }
  };

  const getSeverityIcon = (severity: string, isViewed: boolean = false) => {
    const opacity = isViewed ? 0.6 : 1.0;
    switch (severity?.toLowerCase()) {
      case 'critical':
        return <CriticalIcon sx={{ color: '#f44336', fontSize: 16, opacity }} />;
      case 'warning':
      case 'high':
        return <WarningIcon sx={{ color: '#ff9800', fontSize: 16, opacity }} />;
      case 'info':
      case 'low':
        return <InfoIcon sx={{ color: '#2196f3', fontSize: 16, opacity }} />;
      default:
        return <InfoIcon sx={{ color: '#666666', fontSize: 16, opacity }} />;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Navigation */}
      <Navigation
        open={mobileOpen}
        onClose={handleDrawerToggle}
        isCollapsed={isNavCollapsed}
        onToggleCollapse={() => setIsNavCollapsed(prev => !prev)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${isNavCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          ml: { md: `${isNavCollapsed ? collapsedDrawerWidth : drawerWidth}px` },
          transition: theme.transitions.create(['margin-left', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        {/* Top App Bar */}
        <AppBar
          position="fixed"
          sx={{
            width: { md: `calc(100% - ${isNavCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
            ml: { md: `${isNavCollapsed ? collapsedDrawerWidth : drawerWidth}px` },
            transition: theme.transitions.create(['margin-left', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: 'none',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Toolbar>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Tooltip title={`Robot Alerts (${stats.total})`}>
                  <IconButton
                    onClick={handleAlertMenuOpen}
                    sx={{
                      color: hasActiveVideoCall ? '#1976d2' : '#666666',
                      animation: hasActiveVideoCall ? `${shakeAnimation} 0.7s ease-in-out infinite` : 'none',
                      transformOrigin: 'top center',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                  >
                    <Badge
                      badgeContent={stats.total > 99 ? '99+' : stats.total}
                      color={stats.critical > 0 ? 'error' : stats.total > 0 ? 'warning' : 'default'}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '10px',
                          height: '16px',
                          minWidth: '16px',
                          borderRadius: '8px',
                        },
                      }}
                    >
                      <NotificationIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
                <Typography variant="caption" sx={{ fontSize: '10px', color: '#666666', mt: 0.5 }}>
                  Alerts
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Welcome, {user?.username} ({user?.role_display})
              </Typography>

              <Button color="inherit" startIcon={<LogoutIcon />} onClick={handleLogout} sx={{ textTransform: 'none' }}>
                Logout
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box sx={{ mt: 8 }}>{children}</Box>
      </Box>

      {/* Alert Notification Center */}
      <Menu
        anchorEl={alertMenuAnchor}
        open={Boolean(alertMenuAnchor)}
        onClose={handleAlertMenuClose}
        PaperProps={{
          sx: {
            maxHeight: '600px',
            width: '420px',
            maxWidth: 'calc(100% - 32px)',
            overflowX: 'hidden',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.08)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
            Robot Alerts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stats.total} alert{stats.total === 1 ? '' : 's'} • {stats.critical} critical
          </Typography>
        </Box>

        <Divider />

        {/* Alert List */}
        <Box sx={{ maxHeight: '400px', overflowX: 'hidden', overflowY: 'auto' }}>
          {alerts.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No new alerts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All systems running normally
              </Typography>
            </Box>
          ) : (
            alerts.slice(0, 8).map((alert, index) => {
              const alertLike = alert as AlertLike;
              const alertId = alertLike.id || alertLike.alert_id;
              const viewedAlerts = getViewedAlerts();
              const isViewed = alertId ? viewedAlerts.has(Number(alertId)) : false;
              return (
                <MenuItem
                  key={alertId ?? index}
                  onClick={async () => {
                    // Mark alert as viewed when clicked
                    const alertId = alertLike.id || alertLike.alert_id;
                    if (alertId) {
                      // Use handleAlertClick from alertProcessors
                      handleAlertClick(alertLike as Alert, navigate);
                      await markAlertsViewed([Number(alertId)]);
                    }
                    handleAlertMenuClose();
                  }}
                  sx={{
                    py: 2,
                    px: 3,
                    alignItems: 'flex-start',
                    boxSizing: 'border-box',
                    borderBottom: index < alerts.slice(0, 8).length - 1 ? '1px solid #f0f0f0' : 'none',
                    '&:hover': { backgroundColor: '#f8f9fa' },
                    opacity: isViewed ? 0.7 : 1.0,
                    backgroundColor: isViewed ? '#f8f9fa' : 'transparent',
                    whiteSpace: 'normal',
                    width: '100%',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.25, flexShrink: 0 }}>
                    {getSeverityIcon(alertLike.log?.level || alertLike.severity || 'info')}
                  </ListItemIcon>

                  <ListItemText
                    sx={{ minWidth: 0, m: 0 }}
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 1,
                          mb: 1,
                          minWidth: 0,
                          width: '100%',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'medium',
                            flex: 1,
                            minWidth: 0,
                            lineHeight: 1.4,
                            fontSize: '14px',
                            overflowWrap: 'anywhere',
                            whiteSpace: 'normal',
                          }}
                        >
                          {alertLike.notificationText || alertLike.message || 'Robot Alert'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '11px',
                            ml: 1,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatAlertTime(alertLike.ts || alertLike.timestamp || '')}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
                        <Chip
                          label={alertLike.robot || alertLike.robot_id || 'Robot'}
                          size="small"
                          variant="filled"
                          sx={{
                            fontSize: '11px',
                            height: '20px',
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                          }}
                        />
                        <Chip
                          label={alertLike.museum || alertLike.museum_code || 'Museum'}
                          size="small"
                          variant="filled"
                          sx={{
                            fontSize: '11px',
                            height: '20px',
                            backgroundColor: '#f3e5f5',
                            color: '#7b1fa2',
                          }}
                        />
                        <Chip
                          label={alertLike.log?.level || alertLike.severity || 'info'}
                          size="small"
                          variant="filled"
                          color={
                            (alertLike.log?.level || alertLike.severity || '').toLowerCase() === 'critical'
                              ? 'error'
                              : (alertLike.log?.level || alertLike.severity || '').toLowerCase() === 'warning'
                                ? 'warning'
                                : 'info'
                          }
                          sx={{ fontSize: '11px', height: '20px' }}
                        />
                      </Box>
                    }
                  />
                </MenuItem>
              );
            })
          )}
        </Box>

        {/* Footer */}
        {alerts.length > 0 && <Divider />}
        {alerts.length > 0 && (
          <Box sx={{ p: 2 }}>
            {alerts.length > 8 && (
              <MenuItem
                onClick={handleAlertMenuClose}
                sx={{
                  borderRadius: '8px',
                  mb: 1,
                  backgroundColor: '#f8f9fa',
                  '&:hover': { backgroundColor: '#e9ecef' },
                }}
              >
                <ListItemIcon>
                  <AlertIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="View All Alerts" secondary={`Total: ${alerts.length} alerts`} />
              </MenuItem>
            )}

            <MenuItem sx={{ borderRadius: '8px' }}>
              <ListItemIcon>
                <NotificationIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Alert monitoring active" secondary="View all alerts above" />
            </MenuItem>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default MainLayout;
